import React from 'react';
import {
    Box,
    GridLegacy as Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Stack,
    Divider,
    Alert,
    Avatar,
    Paper,
    useTheme,
    alpha,
    Pagination,
    CircularProgress
} from '@mui/material';
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    FirstPage,
    LastPage
} from '@mui/icons-material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    VideoCall as VideoIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    Schedule as ScheduleIcon,
    Refresh as RefreshIcon,
    AccessTime as TimeIcon,
    CalendarToday as CalendarIcon,
    MeetingRoom as MeetingIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Timer as TimerIcon,
    Groups as TeamsIcon
} from '@mui/icons-material';
import { format, isToday, isTomorrow, isPast, isValid } from 'date-fns';

const SlotsList = ({
    slots,
    activeTab,
    pagination,
    onPageChange,
    onEditSlot,
    onDeleteSlot,
    onRefresh,
    loading
}) => {
    const theme = useTheme();

    // ===== FILTER SLOTS BY TAB =====
    const getFilteredSlots = () => {
        if (!Array.isArray(slots)) {
            console.warn('Slots is not an array:', slots);
            return [];
        }

        switch (activeTab) {
            case 1: // Available Only
                return slots.filter(slot => slot.is_available && !slot.is_booked);
            case 2: // Booked Slots
                return slots.filter(slot => slot.is_booked);
            default: // All Slots
                return slots;
        }
    };

    const filteredSlots = getFilteredSlots();

    // ===== ENHANCED UTILITY FUNCTIONS =====
    const getSlotStatusConfig = (slot) => {
        if (slot.is_booked) return {
            color: '#e74c3c',
            bgColor: alpha('#e74c3c', 0.1),
            label: 'Booked',
            icon: <CheckIcon sx={{ fontSize: 16 }} />
        };
        
        if (!slot.is_available) return {
            color: '#95a5a6',
            bgColor: alpha('#95a5a6', 0.1),
            label: 'Unavailable',
            icon: <CancelIcon sx={{ fontSize: 16 }} />
        };

        if (!slot.start_datetime) return {
            color: '#95a5a6',
            bgColor: alpha('#95a5a6', 0.1),
            label: 'Unknown',
            icon: <TimerIcon sx={{ fontSize: 16 }} />
        };

        try {
            const slotDate = new Date(slot.start_datetime);
            if (!isValid(slotDate)) return {
                color: '#95a5a6',
                bgColor: alpha('#95a5a6', 0.1),
                label: 'Invalid',
                icon: <CancelIcon sx={{ fontSize: 16 }} />
            };

            if (isPast(slotDate)) return {
                color: '#7f8c8d',
                bgColor: alpha('#7f8c8d', 0.1),
                label: 'Past',
                icon: <TimerIcon sx={{ fontSize: 16 }} />
            };

            if (isToday(slotDate)) return {
                color: '#f39c12',
                bgColor: alpha('#f39c12', 0.1),
                label: 'Today',
                icon: <CalendarIcon sx={{ fontSize: 16 }} />
            };

            if (isTomorrow(slotDate)) return {
                color: '#3498db',
                bgColor: alpha('#3498db', 0.1),
                label: 'Tomorrow',
                icon: <CalendarIcon sx={{ fontSize: 16 }} />
            };

            return {
                color: '#27ae60',
                bgColor: alpha('#27ae60', 0.1),
                label: 'Available',
                icon: <CheckIcon sx={{ fontSize: 16 }} />
            };
        } catch (error) {
            return {
                color: '#95a5a6',
                bgColor: alpha('#95a5a6', 0.1),
                label: 'Unknown',
                icon: <CancelIcon sx={{ fontSize: 16 }} />
            };
        }
    };

    const formatSlotTime = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) {
            return { date: 'Invalid Date', timeRange: 'Invalid Time', dayName: '' };
        }

        try {
            const start = new Date(startDateTime);
            const end = new Date(endDateTime);
            if (!isValid(start) || !isValid(end)) {
                return { date: 'Invalid Date', timeRange: 'Invalid Time', dayName: '' };
            }

            const date = format(start, 'MMM dd, yyyy');
            const dayName = format(start, 'EEEE');
            const timeRange = `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
            return { date, timeRange, dayName };
        } catch (error) {
            console.error('Date formatting error:', error, { startDateTime, endDateTime });
            return { date: 'Invalid Date', timeRange: 'Invalid Time', dayName: '' };
        }
    };

    const getMeetingTypeConfig = (meetingType) => {
        switch (meetingType) {
            case 'video':
                return {
                    icon: <VideoIcon sx={{ color: '#3498db' }} />,
                    label: 'Video Call',
                    color: '#3498db'
                };
            case 'phone':
                return {
                    icon: <PhoneIcon sx={{ color: '#e67e22' }} />,
                    label: 'Phone Call',
                    color: '#e67e22'
                };
            case 'in_person':
                return {
                    icon: <LocationIcon sx={{ color: '#8e44ad' }} />,
                    label: 'In Person',
                    color: '#8e44ad'
                };
            default:
                return {
                    icon: <MeetingIcon sx={{ color: '#34495e' }} />,
                    label: 'Meeting',
                    color: '#34495e'
                };
        }
    };

    const calculateDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return 'Unknown';
        try {
            const start = new Date(startDateTime);
            const end = new Date(endDateTime);
            if (!isValid(start) || !isValid(end)) return 'Unknown';
            const diffMinutes = Math.round((end - start) / (1000 * 60));
            
            if (diffMinutes < 60) {
                return `${diffMinutes}min`;
            } else {
                const hours = Math.floor(diffMinutes / 60);
                const minutes = diffMinutes % 60;
                return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
            }
        } catch (error) {
            return 'Unknown';
        }
    };

    // ===== PAGINATION CONTROLS COMPONENT =====
    const PaginationControls = ({ pagination, onPageChange, loading, label }) => {
        if (pagination.total <= pagination.per_page) return null;

        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 3,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid #e0e0e0'
            }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Showing {pagination.showing_start}-{pagination.showing_end} of {pagination.total} {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Page {pagination.page} of {pagination.pages}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.has_prev || loading}
                        onClick={() => onPageChange(1)}
                        startIcon={<FirstPage />}
                        sx={{ minWidth: 'auto', px: 1 }}
                    >
                        First
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.has_prev || loading}
                        onClick={() => onPageChange(pagination.page - 1)}
                        startIcon={<KeyboardArrowLeft />}
                    >
                        Previous
                    </Button>

                    <Box sx={{ mx: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                            const pageNum = Math.max(1, pagination.page - 2) + i;
                            if (pageNum > pagination.pages) return null;

                            return (
                                <Button
                                    key={pageNum}
                                    variant={pageNum === pagination.page ? "contained" : "outlined"}
                                    size="small"
                                    onClick={() => onPageChange(pageNum)}
                                    disabled={loading}
                                    sx={{ minWidth: 40, height: 32 }}
                                >
                                    {pageNum}
                                </Button>
                            );
                        })}
                    </Box>

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.has_next || loading}
                        onClick={() => onPageChange(pagination.page + 1)}
                        endIcon={<KeyboardArrowRight />}
                    >
                        Next
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={!pagination.has_next || loading}
                        onClick={() => onPageChange(pagination.pages)}
                        endIcon={<LastPage />}
                        sx={{ minWidth: 'auto', px: 1 }}
                    >
                        Last
                    </Button>
                </Box>
            </Box>
        );
    };

    // ===== RENDER EMPTY STATE =====
    if (!Array.isArray(slots)) {
        return (
            <Paper 
                elevation={0}
                sx={{
                    p: 6,
                    textAlign: 'center',
                    backgroundColor: alpha('#e74c3c', 0.05),
                    border: `2px dashed ${alpha('#e74c3c', 0.2)}`,
                    borderRadius: 3
                }}
            >
                <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: '#e74c3c', width: 56, height: 56 }}>
                    <CancelIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" color="error" gutterBottom>
                    Data Error
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Slots data is not in the correct format. Expected an array.
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    sx={{
                        background: 'linear-gradient(45deg, #3498db, #2980b9)',
                        '&:hover': { background: 'linear-gradient(45deg, #2980b9, #3498db)' }
                    }}
                >
                    Refresh Data
                </Button>
            </Paper>
        );
    }

    if (filteredSlots.length === 0) {
        const emptyMessage = activeTab === 1
            ? 'No available slots found'
            : activeTab === 2
            ? 'No booked slots found'
            : slots.length === 0
            ? 'No availability slots found'
            : 'No slots match the current filter';

        return (
            <Paper 
                elevation={0}
                sx={{
                    p: 6,
                    textAlign: 'center',
                    backgroundColor: alpha('#3498db', 0.05),
                    border: `2px dashed ${alpha('#3498db', 0.2)}`,
                    borderRadius: 3
                }}
            >
                <Avatar sx={{ mx: 'auto', mb: 2, bgcolor: '#3498db', width: 56, height: 56 }}>
                    <ScheduleIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" color="primary" gutterBottom>
                    {emptyMessage}
                </Typography>
                {slots.length === 0 && activeTab === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create your first availability slot to get started!
                    </Typography>
                )}
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 3
                    }}
                >
                    Refresh Data
                </Button>
            </Paper>
        );
    }

    // ===== RENDER ENHANCED SLOTS LIST =====
    return (
        <Box>
            {/* Enhanced Summary Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                        <ScheduleIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Availability Slots
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Showing {filteredSlots.length} of {pagination.total} slots
                        </Typography>
                    </Box>
                </Box>
                <Tooltip title="Refresh slots">
                    <IconButton
                        onClick={onRefresh}
                        sx={{
                            color: 'white',
                            bgcolor: alpha('#fff', 0.1),
                            '&:hover': { bgcolor: alpha('#fff', 0.2) }
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Paper>

            {/* Enhanced Slots Grid */}
            <Grid container spacing={3}>
                {filteredSlots.map((slot, index) => {
                    const { date, timeRange, dayName } = formatSlotTime(slot.start_datetime, slot.end_datetime);
                    const statusConfig = getSlotStatusConfig(slot);
                    const meetingConfig = getMeetingTypeConfig(slot.meeting_type);
                    const duration = calculateDuration(slot.start_datetime, slot.end_datetime);

                    return (
                        <Grid item xs={12} sm={6} lg={4} key={slot.id || index}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    border: `1px solid ${alpha('#000', 0.08)}`,
                                    borderRadius: 3,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'visible',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme.shadows[8],
                                        borderColor: statusConfig.color
                                    }
                                }}
                            >
                                {/* Status Indicator Bar */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: 4,
                                        background: `linear-gradient(90deg, ${statusConfig.color}, ${alpha(statusConfig.color, 0.7)})`,
                                        borderRadius: '12px 12px 0 0'
                                    }}
                                />

                                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {/* Header Section */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: '1.1rem',
                                                    color: '#2c3e50',
                                                    mb: 0.5,
                                                    lineHeight: 1.3
                                                }}
                                            >
                                                {slot.title || 'Available for Interview'}
                                            </Typography>
                                            <Chip
                                                icon={statusConfig.icon}
                                                label={statusConfig.label}
                                                size="small"
                                                sx={{
                                                    bgcolor: statusConfig.bgColor,
                                                    color: statusConfig.color,
                                                    fontWeight: 500,
                                                    borderRadius: 2
                                                }}
                                            />
                                        </Box>

                                        {/* Action Buttons */}
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Edit slot">
                                                <IconButton
                                                    onClick={() => onEditSlot(slot)}
                                                    disabled={slot.is_booked}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha('#3498db', 0.1),
                                                        color: '#3498db',
                                                        '&:hover': { bgcolor: alpha('#3498db', 0.2) },
                                                        '&:disabled': { bgcolor: alpha('#bdc3c7', 0.1), color: '#bdc3c7' }
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete slot">
                                                <IconButton
                                                    onClick={() => onDeleteSlot(slot.id)}
                                                    disabled={slot.is_booked}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha('#e74c3c', 0.1),
                                                        color: '#e74c3c',
                                                        '&:hover': { bgcolor: alpha('#e74c3c', 0.2) },
                                                        '&:disabled': { bgcolor: alpha('#bdc3c7', 0.1), color: '#bdc3c7' }
                                                    }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>

                                    {/* Time Information */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            bgcolor: alpha('#f8f9fa', 0.7),
                                            borderRadius: 2,
                                            border: `1px solid ${alpha('#000', 0.05)}`
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <CalendarIcon sx={{ fontSize: 16, color: '#7f8c8d' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                                                    {dayName}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#7f8c8d', fontSize: '0.875rem' }}>
                                                {date}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <TimeIcon sx={{ fontSize: 16, color: '#e67e22' }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                                        {timeRange}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={duration}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha('#e67e22', 0.1),
                                                        color: '#e67e22',
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    {/* Meeting Details */}
                                    <Box sx={{ mb: 2 }}>
                                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                                            {meetingConfig.icon}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: meetingConfig.color
                                                }}
                                            >
                                                {meetingConfig.label}
                                            </Typography>
                                            {slot.slot_type === 'weekly_recurring' && (
                                                <Chip
                                                    label="Recurring"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha('#9b59b6', 0.1),
                                                        color: '#9b59b6',
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            )}
                                            {slot.teams_join_url && (
                                                <Chip
                                                    icon={<TeamsIcon sx={{ fontSize: 14 }} />}
                                                    label="Teams"
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha('#6264a7', 0.1),
                                                        color: '#6264a7',
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            )}
                                        </Stack>

                                        {slot.location && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <LocationIcon sx={{ fontSize: 16, color: '#95a5a6' }} />
                                                <Typography variant="body2" sx={{ color: '#7f8c8d', fontSize: '0.875rem' }}>
                                                    {slot.location}
                                                </Typography>
                                            </Box>
                                        )}

                                        {slot.description && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#7f8c8d',
                                                    fontSize: '0.875rem',
                                                    fontStyle: 'italic',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {slot.description}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Booking Information */}
                                    {slot.is_booked && slot.booking_info && (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                bgcolor: alpha('#e74c3c', 0.05),
                                                border: `1px solid ${alpha('#e74c3c', 0.2)}`,
                                                borderRadius: 2
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: '#e74c3c',
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                <PersonIcon sx={{ fontSize: 16 }} />
                                                Interview Booked
                                            </Typography>
                                            {slot.booking_info.candidate_name && (
                                                <Typography variant="body2" sx={{ color: '#2c3e50', mb: 0.5 }}>
                                                    <strong>Candidate:</strong> {slot.booking_info.candidate_name}
                                                </Typography>
                                            )}
                                            {slot.booking_info.job_title && (
                                                <Typography variant="body2" sx={{ color: '#2c3e50', mb: 0.5 }}>
                                                    <strong>Position:</strong> {slot.booking_info.job_title}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                                                <strong>Status:</strong> {slot.booking_info.interview_status || 'Scheduled'}
                                            </Typography>
                                        </Paper>
                                    )}

                                    {/* Teams Meeting Link */}
                                    {slot.teams_join_url && (
                                        <Box sx={{ mt: 'auto' }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                startIcon={<TeamsIcon />}
                                                onClick={() => window.open(slot.teams_join_url, '_blank')}
                                                sx={{
                                                    background: 'linear-gradient(45deg, #6264a7, #464775)',
                                                    color: 'white',
                                                    textTransform: 'none',
                                                    borderRadius: 2,
                                                    py: 1,
                                                    fontWeight: 500,
                                                    '&:hover': {
                                                        background: 'linear-gradient(45deg, #464775, #6264a7)',
                                                        transform: 'translateY(-1px)'
                                                    }
                                                }}
                                            >
                                                Join Teams Meeting
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Available Slot Actions */}
                                    {slot.is_available && !slot.is_booked && !isPast(new Date(slot.start_datetime || '')) && (
                                        <Box sx={{ mt: 'auto' }}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<EditIcon />}
                                                onClick={() => onEditSlot(slot)}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: 2,
                                                    py: 1,
                                                    fontWeight: 500,
                                                    borderColor: alpha('#3498db', 0.5),
                                                    color: '#3498db',
                                                    '&:hover': {
                                                        bgcolor: alpha('#3498db', 0.05),
                                                        borderColor: '#3498db',
                                                        transform: 'translateY(-1px)'
                                                    }
                                                }}
                                            >
                                                Edit Slot Details
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Pagination Controls */}
            <PaginationControls
                pagination={pagination}
                onPageChange={onPageChange}
                loading={loading}
                label="slots"
            />
        </Box>
    );
};

export default SlotsList;
